/**
 * User list component
 */

import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card } from '../UI/Card';
import { Button } from '../UI/Button';
import { Input } from '../UI/Input';
import { Badge } from '../UI/Badge';
import { listUsers, deleteUser, type User } from '../../api/users';
import { inviteUser, type InviteUserRequest } from '../../api/teams';
import { listRoles } from '../../api/rbac';
import { useWorkspace } from '../../hooks/useWorkspace';
import { useToastStore } from '../../stores/toast';
import { Users, Search, UserPlus, Edit, Trash2, Mail, User as UserIcon, Shield, CheckCircle, XCircle, X } from 'lucide-react';

export function UserList() {
  const { tenantId, projectId } = useWorkspace();
  const queryClient = useQueryClient();
  const { addToast } = useToastStore();
  const [searchTerm, setSearchTerm] = useState('');
  const [showInviteModal, setShowInviteModal] = useState(false);

  const { data, isLoading, error } = useQuery({
    queryKey: ['users', tenantId, projectId],
    queryFn: () => listUsers({
      tenant_id: tenantId || undefined,
      project_id: projectId || undefined,
      active_only: false,
    }),
    enabled: true,
  });

  const deleteUserMutation = useMutation({
    mutationFn: deleteUser,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      addToast({
        message: 'User deleted successfully',
        type: 'success',
      });
    },
    onError: (error: any) => {
      addToast({
        message: error.response?.data?.detail || 'Failed to delete user',
        type: 'error',
      });
    },
  });

  const filteredUsers = data?.users.filter(user =>
    user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.full_name?.toLowerCase().includes(searchTerm.toLowerCase())
  ) || [];

  if (isLoading) {
    return (
      <Card title="Users">
        <div className="text-center py-8 text-gray-500 dark:text-gray-400">Loading users...</div>
      </Card>
    );
  }

  if (error) {
    return (
      <Card title="Users">
        <div className="text-center py-8 text-red-500 dark:text-red-400">Error loading users</div>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
        <div className="space-y-4">
          {/* Search and Actions */}
          <div className="flex items-center gap-4">
            <div className="flex-1">
              <Input
                type="text"
                placeholder="Search users by name, email, or username..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                leftIcon={<Search className="h-5 w-5 text-gray-400" />}
              />
            </div>
            <Button
              onClick={() => setShowInviteModal(true)}
              className="whitespace-nowrap"
            >
              <UserPlus className="h-5 w-5" />
              Invite User
            </Button>
          </div>

          {/* Users Table */}
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200 dark:border-slate-700">
                  <th className="text-left py-3 px-4 font-semibold text-gray-700 dark:text-gray-300">User</th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-700 dark:text-gray-300">Email</th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-700 dark:text-gray-300">Status</th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-700 dark:text-gray-300">Last Login</th>
                  <th className="text-right py-3 px-4 font-semibold text-gray-700 dark:text-gray-300">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="text-center py-8 text-gray-500 dark:text-gray-400">
                      No users found
                    </td>
                  </tr>
                ) : (
                  filteredUsers.map((user) => (
                    <tr key={user.id} className="border-b border-gray-100 dark:border-slate-700 hover:bg-gray-50 dark:hover:bg-slate-800/50">
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white font-semibold">
                            {user.full_name?.[0]?.toUpperCase() || user.username[0].toUpperCase()}
                          </div>
                          <div>
                            <div className="font-medium text-gray-900 dark:text-gray-100">
                              {user.full_name || user.username}
                            </div>
                            <div className="text-sm text-gray-500 dark:text-gray-400">@{user.username}</div>
                          </div>
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-2 text-gray-700 dark:text-gray-300">
                          <Mail className="h-4 w-4 text-gray-400 dark:text-gray-500" />
                          {user.email}
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-2">
                          {user.is_active ? (
                            <Badge variant="success">
                              <CheckCircle className="h-3 w-3" />
                              Active
                            </Badge>
                          ) : (
                            <Badge variant="error">
                              <XCircle className="h-3 w-3" />
                              Inactive
                            </Badge>
                          )}
                          {user.is_email_verified ? (
                            <Badge variant="info">
                              <Shield className="h-3 w-3" />
                              Verified
                            </Badge>
                          ) : (
                            <Badge variant="warning">Unverified</Badge>
                          )}
                        </div>
                      </td>
                      <td className="py-3 px-4 text-sm text-gray-600 dark:text-gray-400">
                        {user.last_login_at
                          ? new Date(user.last_login_at).toLocaleDateString()
                          : 'Never'}
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => {
                              // TODO: Navigate to user detail/edit
                            }}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => {
                              if (window.confirm(`Are you sure you want to delete ${user.email}?`)) {
                                deleteUserMutation.mutate(user.id);
                              }
                            }}
                            disabled={deleteUserMutation.isPending}
                          >
                            <Trash2 className="h-4 w-4 text-red-600 dark:text-red-400" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Summary */}
          <div className="flex items-center justify-between pt-4 border-t border-gray-200 dark:border-slate-700">
            <div className="text-sm text-gray-600 dark:text-gray-400">
              Showing {filteredUsers.length} of {data?.total || 0} users
            </div>
          </div>
        </div>

      {/* Invite Modal */}
      {showInviteModal && (
        <InviteUserModal
          tenantId={tenantId}
          projectId={projectId}
          onClose={() => setShowInviteModal(false)}
          onSuccess={() => {
            setShowInviteModal(false);
            queryClient.invalidateQueries({ queryKey: ['users'] });
            addToast({
              message: 'User invitation sent successfully',
              type: 'success',
            });
          }}
        />
      )}
    </div>
  );
}

function InviteUserModal({ 
  tenantId, 
  projectId, 
  onClose, 
  onSuccess 
}: { 
  tenantId?: string; 
  projectId?: string; 
  onClose: () => void; 
  onSuccess: () => void;
}) {
  const [formData, setFormData] = useState<InviteUserRequest>({
    email: '',
    tenant_id: tenantId || undefined,
    project_id: projectId || undefined,
    role_id: undefined,
  });

  const { data: roles, isLoading: rolesLoading } = useQuery({
    queryKey: ['roles', tenantId, projectId],
    queryFn: () => listRoles({
      tenant_id: tenantId || undefined,
      project_id: projectId || undefined,
    }),
    enabled: true,
  });

  const inviteMutation = useMutation({
    mutationFn: inviteUser,
    onSuccess: () => {
      onSuccess();
    },
    onError: (error: any) => {
      addToast({
        message: error?.response?.data?.detail || 'Failed to send invitation',
        type: 'error',
      });
    },
  });

  const { addToast } = useToastStore();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.email) {
      addToast({ type: 'error', message: 'Email is required' });
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      addToast({ type: 'error', message: 'Please enter a valid email address' });
      return;
    }

    if (!formData.tenant_id && !formData.project_id) {
      addToast({ type: 'error', message: 'Either tenant or project must be selected' });
      return;
    }

    inviteMutation.mutate(formData);
  };

  return (
    <div 
      className="fixed inset-0 bg-black/50 dark:bg-black/70 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div 
        className="bg-white dark:bg-slate-800 rounded-lg shadow-xl max-w-md w-full"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 dark:border-slate-700 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Invite User</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Email Address <span className="text-red-500">*</span>
            </label>
            <Input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              placeholder="user@example.com"
              required
              className="w-full"
            />
          </div>

          {!tenantId && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Tenant ID (optional)
              </label>
              <Input
                type="text"
                value={formData.tenant_id || ''}
                onChange={(e) => setFormData({ ...formData, tenant_id: e.target.value || undefined })}
                placeholder="Leave empty for system-wide"
                className="w-full"
              />
            </div>
          )}

          {!projectId && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Project ID (optional)
              </label>
              <Input
                type="text"
                value={formData.project_id || ''}
                onChange={(e) => setFormData({ ...formData, project_id: e.target.value || undefined })}
                placeholder="Leave empty for tenant-wide"
                className="w-full"
              />
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Role (optional)
            </label>
            {rolesLoading ? (
              <div className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg bg-gray-50 dark:bg-slate-800 text-gray-500 dark:text-gray-400">
                Loading roles...
              </div>
            ) : (
              <select
                value={formData.role_id || ''}
                onChange={(e) => setFormData({ ...formData, role_id: e.target.value || undefined })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">No role assigned</option>
                {roles && roles.length > 0 ? (
                  roles.map((role) => (
                    <option key={role.id} value={role.id}>
                      {role.display_name} ({role.scope})
                    </option>
                  ))
                ) : (
                  <option value="" disabled>No roles available</option>
                )}
              </select>
            )}
          </div>

          <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200 dark:border-slate-700">
            <Button
              type="button"
              variant="ghost"
              onClick={onClose}
              disabled={inviteMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={inviteMutation.isPending || !formData.email}
            >
              {inviteMutation.isPending ? 'Sending...' : 'Send Invitation'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}


